import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { formatReadableOfferId, formatReadableBookingId, getToday, getFactoryShortCode } from '../../../utils/idGenerator';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d?.getTime())) return dateStr;
  return d?.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const SugarBookingModal = ({ isOpen, onClose, purchaseData, onConfirm, quotaStatus }) => {
  const navigate = useNavigate();
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [basicRatePerTon, setBasicRatePerTon] = useState('');
  const [basicRateError, setBasicRateError] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
  const [amountPaidError, setAmountPaidError] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankNameError, setBankNameError] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [utrError, setUtrError] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleError, setVehicleError] = useState('');
  const [driverMobile, setDriverMobile] = useState('');
  const [driverMobileError, setDriverMobileError] = useState('');
  const [tdsApplicable, setTdsApplicable] = useState(null);
  const [tdsError, setTdsError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  if (!isOpen || !purchaseData) return null;

  const isPendingApproval = quotaStatus === 'pending_approval' || purchaseData?.availabilityStatus === 'pending_approval';

  const quantity = purchaseData?.selectedQuantity || purchaseData?.quantity;
  const totalAmount = purchaseData?.totalAmount || (purchaseData?.rate * quantity * 1.05);
  const tokenPayment = totalAmount * 0.3;
  const remainingPayment = totalAmount * 0.7;

  const handleConfirm = async () => {
    let hasError = false;

    if (!basicRatePerTon || Number(basicRatePerTon) <= 0) {
      setBasicRateError('Enter a valid rate per ton'); hasError = true;
    } else setBasicRateError('');

    if (!amountPaid || Number(amountPaid) <= 0) {
      setAmountPaidError('Enter exact amount you transferred'); hasError = true;
    } else setAmountPaidError('');

    if (!bankName?.trim()) {
      setBankNameError('Enter your bank name'); hasError = true;
    } else setBankNameError('');

    if (!utrNumber?.trim()) {
      setUtrError('Enter your UTR number'); hasError = true;
    } else setUtrError('');

    if (!vehicleNumber?.trim()) {
      setVehicleError('Enter vehicle number'); hasError = true;
    } else setVehicleError('');

    if (!driverMobile?.trim() || driverMobile?.trim()?.length !== 10) {
      setDriverMobileError('Enter a valid 10-digit mobile number'); hasError = true;
    } else setDriverMobileError('');

    if (tdsApplicable === null) {
      setTdsError('Please select whether TDS is applicable'); hasError = true;
    } else setTdsError('');

    if (!termsAccepted) {
      setTermsError('Please accept the Terms & Conditions to proceed'); hasError = true;
    } else setTermsError('');

    if (hasError) return;

    setSaving(true);
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

      if (sessionError) {
        console.error('Session error:', sessionError);
      }

      if (session?.user?.id) {
        let freshStatus = purchaseData?.availabilityStatus || quotaStatus || 'available';
        if (purchaseData?.id) {
          const { data: quota } = await supabase
            ?.from('quotas')
            ?.select('availability_status')
            ?.eq('id', purchaseData?.id)
            ?.single();
          if (quota?.availability_status) freshStatus = quota?.availability_status;
        }

        const bookingStatus = freshStatus === 'pending_approval' ? 'pending_approval' : 'pending';

        const factoryCode = await getFactoryShortCode(purchaseData?.millName);
        const buyerCode = (session?.user?.email || 'BUYER')
          ?.split('@')?.[0]?.toUpperCase()?.slice(0, 6);
        const today = getToday();
        const qty = Math.floor(Number(quantity) || 0);

        const readableOfferId = formatReadableOfferId(factoryCode, today, 1);
        const readableBookingId = formatReadableBookingId(buyerCode, factoryCode, today, qty);

        // All financial calculations use offer rate — buyer-entered rate is informational only
        const offerRate = Number(purchaseData?.rate);
        const basicAmount = Number(quantity) * offerRate;
        const gstAmount = basicAmount * 0.05;
        const calcTotalAmount = basicAmount + gstAmount;
        const tdsAmount = tdsApplicable ? Math.round(basicAmount * 0.001 * 100) / 100 : 0;
        const netPayable = Math.round((calcTotalAmount - tdsAmount) * 100) / 100;

        const insertPayload = {
          buyer_id: session?.user?.id,
          buyer_email: session?.user?.email || '',
          quota_id: purchaseData?.id || null,
          offer_id: purchaseData?.offerId || '',
          factory_name: purchaseData?.millName || '',
          grade: purchaseData?.grade || '',
          quantity: Number(quantity) || 0,
          rate_per_ton: offerRate,
          basic_rate_per_ton: Number(basicRatePerTon) || offerRate,
          loading_from: purchaseData?.deliveryFrom || purchaseData?.city || '',
          destination: '',
          no_of_trucks: 1,
          total_amount: calcTotalAmount,
          booking_type: 'sugar_only',
          booking_date: new Date()?.toISOString(),
          status: bookingStatus,
          readable_offer_id: readableOfferId,
          readable_booking_id: readableBookingId,
          assigned_vehicle_number: vehicleNumber?.trim() || '',
          driver_mobile: driverMobile?.trim() || '',
          tds_applicable: tdsApplicable,
          tds_amount: tdsAmount,
          net_payable: netPayable,
        };

        const { data: insertedData, error: insertError } = await supabase
          ?.from('buyer_bookings')
          ?.insert(insertPayload)
          ?.select();

        if (insertError) {
          console.error('Booking save error:', insertError?.message, insertError?.code, insertError?.details, insertError?.hint);
        } else {
          console.log('Booking saved successfully:', insertedData);

          // Insert trade payment
          try {
            await supabase
              ?.from('trade_payments')
              ?.insert({
                booking_id: insertedData?.[0]?.id,
                payment_type: 'buyer_advance',
                payment_method: 'RTGS',
                amount: Number(amountPaid),
                utr_number: utrNumber?.trim() || '',
                transaction_reference: utrNumber?.trim() || '',
                payer_bank_account: bankName?.trim() || '',
                paid_by: session?.user?.user_metadata?.full_name || session?.user?.email,
                paid_to: 'Villgrains FarmTech Pvt Ltd',
                status: 'pending',
                currency: 'INR',
              });
          } catch (paymentErr) {
            console.error('Trade payment insert error:', paymentErr);
          }

          // Send PO email — rate_per_ton uses offer rate for balance calculation
          try {
            await supabase.functions.invoke('send-booking-email', {
              body: {
                buyer_email: session?.user?.email,
                buyer_name: session?.user?.user_metadata?.full_name || session?.user?.email,
                readable_booking_id: readableBookingId,
                readable_offer_id: readableOfferId,
                factory_name: purchaseData?.millName,
                grade: purchaseData?.grade,
                quantity: Number(quantity),
                rate_per_ton: offerRate,
                total_amount: calcTotalAmount,
                booking_type: 'sugar_only',
                vehicle_number: vehicleNumber?.trim() || '',
                driver_mobile: driverMobile?.trim() || '',
                bank_name: bankName?.trim() || '',
                utr_number: utrNumber?.trim() || '',
                amount_paid: Number(amountPaid),
                tds_applicable: tdsApplicable,
                tds_amount: tdsAmount,
                net_payable: netPayable,
              },
            });
          } catch (emailErr) {
            console.error('PO email error:', emailErr);
          }
        }
      } else {
        console.warn('No authenticated session found — booking not saved');
      }
    } catch (err) {
      console.error('handleConfirm error:', err?.message);
    } finally {
      setSaving(false);
      setConfirmed(true);
      onConfirm?.();
    }
  };

  // Under Review screen
  if (confirmed && isPendingApproval) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="rounded-3xl shadow-2xl w-full max-w-md overflow-y-auto" style={{ backgroundColor: '#ffffff', maxHeight: '90vh' }}>
          <div className="px-6 pt-6 pb-4 text-center" style={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)' }}>
              <Icon name="Clock" size={28} color="#ffffff" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">Approval Pending</h2>
            <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.95)' }}>Your Sugar booking has been submitted and is awaiting approval.</p>
          </div>
          <div className="px-5 py-4">
            <div className="rounded-2xl p-4 mb-3 space-y-2" style={{ backgroundColor: '#fffbeb', border: '1.5px solid #fde68a' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#92400e' }}>Booking Summary</p>
              {purchaseData?.offerId && (
                <div className="flex justify-between items-center text-sm">
                  <span style={{ color: '#6b7280' }}>Offer ID</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.offerId}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Factory</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.millName}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Grade</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.grade}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Quantity</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{quantity} MT</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Rate per Ton</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(purchaseData?.rate)?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between items-center text-sm border-t pt-2" style={{ borderColor: '#fde68a' }}>
                <span style={{ color: '#6b7280' }}>Total Amount</span>
                <span className="font-bold text-base" style={{ color: '#d97706' }}>₹{Number(totalAmount)?.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="rounded-xl p-3 mb-4 flex items-center gap-2" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <Icon name="Clock" size={14} color="#d97706" />
              <p className="text-xs font-medium" style={{ color: '#92400e' }}>Our team will confirm your booking within <strong>1 hour</strong></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setConfirmed(false); onClose(); navigate('/buyer-operations-dashboard'); }}
                className="py-2.5 rounded-xl text-sm font-bold border-2 transition-all"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                View Booking
              </button>
              <button
                onClick={() => { setConfirmed(false); onClose(); navigate('/buyer-operations-dashboard'); }}
                className="py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                style={{ backgroundColor: '#d97706' }}
              >
                Go to Buyer Dashboard
              </button>
            </div>
            <button
              onClick={() => { setConfirmed(false); onClose(); }}
              className="w-full mt-2 py-2 text-xs font-medium rounded-xl"
              style={{ color: '#9ca3af', backgroundColor: '#f9fafb' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Booking Confirmed screen
  if (confirmed && !isPendingApproval) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="rounded-3xl shadow-2xl w-full max-w-md overflow-y-auto" style={{ backgroundColor: '#ffffff', maxHeight: '90vh' }}>
          <div className="px-6 pt-6 pb-4 text-center" style={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)' }}>
              <Icon name="Clock" size={28} color="#ffffff" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">Approval Pending</h2>
            <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.95)' }}>Your Sugar booking has been submitted and is awaiting approval.</p>
          </div>
          <div className="px-5 py-4">
            <div className="rounded-2xl p-4 mb-3 space-y-2" style={{ backgroundColor: '#fffbeb', border: '1.5px solid #fde68a' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#92400e' }}>Booking Summary</p>
              {purchaseData?.offerId && (
                <div className="flex justify-between items-center text-sm">
                  <span style={{ color: '#6b7280' }}>Offer ID</span>
                  <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.offerId}</span>
                </div>
              )}
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Factory</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.millName}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Grade</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.grade}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Quantity</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>{quantity} MT</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span style={{ color: '#6b7280' }}>Rate per Ton</span>
                <span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(purchaseData?.rate)?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between items-center text-sm border-t pt-2" style={{ borderColor: '#fde68a' }}>
                <span style={{ color: '#6b7280' }}>Total Amount</span>
                <span className="font-bold text-base" style={{ color: '#d97706' }}>₹{Number(totalAmount)?.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div className="rounded-xl p-3 mb-4 flex items-center gap-2" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <Icon name="Clock" size={14} color="#d97706" />
              <p className="text-xs font-medium" style={{ color: '#92400e' }}>Our team will confirm your booking within <strong>1 hour</strong></p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setConfirmed(false); onClose(); navigate('/buyer-operations-dashboard'); }}
                className="py-2.5 rounded-xl text-sm font-bold border-2 transition-all"
                style={{ borderColor: '#d97706', color: '#d97706', backgroundColor: '#fffbeb' }}
              >
                View Booking
              </button>
              <button
                onClick={() => { setConfirmed(false); onClose(); navigate('/buyer-operations-dashboard'); }}
                className="py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                style={{ backgroundColor: '#d97706' }}
              >
                Go to Buyer Dashboard
              </button>
            </div>
            <button
              onClick={() => { setConfirmed(false); onClose(); }}
              className="w-full mt-2 py-2 text-xs font-medium rounded-xl"
              style={{ color: '#9ca3af', backgroundColor: '#f9fafb' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-100 to-orange-100 border-b border-amber-200 p-6 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-400/30 rounded-lg flex items-center justify-center">
                <Icon name="Package" size={24} className="text-amber-700" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Review Purchase</h3>
                <p className="text-sm text-gray-600 mt-1">Confirm your order details</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800 transition-colors">
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Pending Approval Notice */}
        {isPendingApproval && (
          <div className="mx-6 mt-4 rounded-xl px-4 py-3 flex items-start gap-2" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
            <Icon name="Clock" size={16} color="#d97706" />
            <p className="text-xs" style={{ color: '#92400e' }}>
              <strong>Note:</strong> This offer is currently under review by our Quota Manager team. Your booking will be submitted for approval.
            </p>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-4">

          {/* Order Summary */}
          <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
            <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: '#92400e' }}>Order Summary</p>
            <div className="flex justify-between text-sm">
              <span style={{ color: '#6b7280' }}>Factory</span>
              <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.millName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: '#6b7280' }}>Grade</span>
              <span className="font-semibold" style={{ color: '#1f2937' }}>{purchaseData?.grade}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: '#6b7280' }}>Quantity</span>
              <span className="font-semibold" style={{ color: '#1f2937' }}>{quantity} MT</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: '#6b7280' }}>Rate / MT</span>
              <span className="font-semibold" style={{ color: '#1f2937' }}>₹{Number(purchaseData?.rate)?.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: '#6b7280' }}>GST (5%)</span>
              <span className="font-semibold" style={{ color: '#1f2937' }}>₹{(purchaseData?.rate * quantity * 0.05)?.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm border-t pt-2" style={{ borderColor: '#fde68a' }}>
              <span className="font-bold" style={{ color: '#92400e' }}>Total (Incl GST)</span>
              <span className="font-bold text-base" style={{ color: '#d97706' }}>₹{Number(totalAmount)?.toLocaleString('en-IN')}</span>
            </div>
          </div>

          {/* Basic Rate per Ton */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
              Basic Rate per Ton <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p className="text-xs" style={{ color: '#9ca3af' }}>Enter the rate you are paying to SugarHathi</p>
            <input
              type="number"
              placeholder={`e.g. ${purchaseData?.rate}`}
              value={basicRatePerTon}
              onChange={e => { setBasicRatePerTon(e?.target?.value); setBasicRateError(''); }}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: basicRateError ? '#ef4444' : '#d1d5db', color: '#1f2937' }}
            />
            {basicRateError && <p className="text-xs" style={{ color: '#dc2626' }}>{basicRateError}</p>}
          </div>

          {/* Amount Paid */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
              Amount Paid <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p className="text-xs" style={{ color: '#9ca3af' }}>Enter exact amount you transferred</p>
            <input
              type="number"
              placeholder="e.g. 1245000"
              value={amountPaid}
              onChange={e => { setAmountPaid(e?.target?.value); setAmountPaidError(''); }}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: amountPaidError ? '#ef4444' : '#d1d5db', color: '#1f2937' }}
            />
            {amountPaidError && <p className="text-xs" style={{ color: '#dc2626' }}>{amountPaidError}</p>}
          </div>

          {/* Bank Name */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
              Bank Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p className="text-xs" style={{ color: '#9ca3af' }}>Eg: ICICI Bank, HDFC Bank</p>
            <input
              type="text"
              placeholder="e.g. ICICI Bank"
              value={bankName}
              onChange={e => { setBankName(e?.target?.value); setBankNameError(''); }}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: bankNameError ? '#ef4444' : '#d1d5db', color: '#1f2937' }}
            />
            {bankNameError && <p className="text-xs" style={{ color: '#dc2626' }}>{bankNameError}</p>}
          </div>

          {/* UTR Number */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
              UTR Number <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p className="text-xs" style={{ color: '#9ca3af' }}>Enter exact UTR number from your bank</p>
            <input
              type="text"
              placeholder="e.g. ICICIUTR0001234567"
              value={utrNumber}
              onChange={e => { setUtrNumber(e?.target?.value?.toUpperCase()); setUtrError(''); }}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: utrError ? '#ef4444' : '#d1d5db', color: '#1f2937' }}
            />
            {utrError && <p className="text-xs" style={{ color: '#dc2626' }}>{utrError}</p>}
          </div>

          {/* Vehicle Number */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
              Vehicle Number <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p className="text-xs" style={{ color: '#9ca3af' }}>e.g. KA06AC9637</p>
            <input
              type="text"
              placeholder="e.g. MH09AB1234"
              value={vehicleNumber}
              onChange={e => { setVehicleNumber(e?.target?.value?.toUpperCase()); setVehicleError(''); }}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: vehicleError ? '#ef4444' : '#d1d5db', color: '#1f2937' }}
            />
            {vehicleError && <p className="text-xs" style={{ color: '#dc2626' }}>{vehicleError}</p>}
          </div>

          {/* Driver Mobile */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
              Driver Mobile <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p className="text-xs" style={{ color: '#9ca3af' }}>Enter 10 digit driver mobile number</p>
            <input
              type="tel"
              placeholder="e.g. 9876543210"
              value={driverMobile}
              onChange={e => { setDriverMobile(e?.target?.value); setDriverMobileError(''); }}
              maxLength={10}
              className="w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: driverMobileError ? '#ef4444' : '#d1d5db', color: '#1f2937' }}
            />
            {driverMobileError && <p className="text-xs" style={{ color: '#dc2626' }}>{driverMobileError}</p>}
          </div>

          {/* TDS */}
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold" style={{ color: '#374151' }}>
              TDS Applicable u/s 194Q <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p className="text-xs" style={{ color: '#9ca3af' }}>Select Yes if your annual purchases exceed ₹50 Lakhs</p>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tds"
                  checked={tdsApplicable === true}
                  onChange={() => { setTdsApplicable(true); setTdsError(''); }}
                  className="w-4 h-4 accent-amber-500"
                />
                <span className="text-sm font-medium" style={{ color: '#1f2937' }}>Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tds"
                  checked={tdsApplicable === false}
                  onChange={() => { setTdsApplicable(false); setTdsError(''); }}
                  className="w-4 h-4 accent-amber-500"
                />
                <span className="text-sm font-medium" style={{ color: '#1f2937' }}>No</span>
              </label>
            </div>
            {tdsError && <p className="text-xs" style={{ color: '#dc2626' }}>{tdsError}</p>}
          </div>

          {/* Net Payable — shown after TDS selection */}
          {tdsApplicable !== null && (
            <div className="rounded-xl p-4" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              {tdsApplicable ? (
                <>
                  <div className="flex justify-between text-sm mb-1">
                    <span style={{ color: '#6b7280' }}>TDS u/s 194Q @ 0.1%</span>
                    <span style={{ color: '#dc2626' }}>- ₹{(Math.round(purchaseData?.rate * quantity * 0.001 * 100) / 100)?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-bold text-sm" style={{ color: '#065f46' }}>Net Payable (After TDS)</span>
                    <span className="font-bold text-base" style={{ color: '#059669' }}>
                      ₹{(Math.round((totalAmount - Math.round(purchaseData?.rate * quantity * 0.001 * 100) / 100) * 100) / 100)?.toLocaleString('en-IN')}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="font-bold text-sm" style={{ color: '#065f46' }}>Amount Payable</span>
                  <span className="font-bold text-base" style={{ color: '#059669' }}>₹{Number(totalAmount)?.toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>
          )}

          {/* T&C Checkbox */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={e => { setTermsAccepted(e?.target?.checked); setTermsError(''); }}
              className="w-4 h-4 mt-0.5 accent-amber-500 flex-shrink-0"
            />
            <span className="text-xs" style={{ color: '#374151' }}>
              I agree to the{' '}
              <a href="https://sugarhathi.com/buyerterms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold"
                style={{ color: '#d97706' }}
              >
                Buyer Terms & Conditions
              </a>
            </span>
          </label>
          {termsError && <p className="text-xs" style={{ color: '#dc2626' }}>{termsError}</p>}

        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-amber-50 border-t border-amber-200 p-6 rounded-b-xl">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              fullWidth
              className="h-13 px-6 py-3 text-base font-bold border-2 border-gray-400 text-gray-600 hover:bg-gray-100 bg-transparent"
            >
              Back
            </Button>
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={saving}
              iconName={saving ? undefined : (isPendingApproval ? 'Send' : 'CheckCircle')}
              iconPosition="right"
              fullWidth
              className="h-13 px-8 py-3 text-base font-extrabold bg-amber-400 hover:bg-amber-500 text-gray-900 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" />
                  Saving...
                </span>
              ) : isPendingApproval ? 'Submit for Approval' : 'Confirm Purchase'}
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SugarBookingModal;


